import React, { PureComponent, ReactNode } from 'react';
import { Flex } from '../../common/Flex';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { Button, notification, Select, Space, Spin } from 'antd';
import {
    BOOK_SLOT_IN_REGULATION_DTO_CLAZZ_VALUE,
    BookSlotInRegulationDto,
    CfmuFlightType,
    CREATE_SLOT_IN_REGULATION_DTO_CLAZZ_VALUE,
    CreateSlotInRegulationDto,
    FlightId,
    FlightResultList,
    ForceEntryType,
    FREE_FLIGHT_FROM_SLOT_DTO_CLAZZ_VALUE,
    FreeFlightFromSlotDto,
    MeasureId,
    Reply,
    RetrieveSlotListsRequestDto,
    Slot,
    SlotType,
    SlotUpdateKind,
    SWAP_TWO_FLIGHTS_DTO_CLAZZ_VALUE,
    SwapTwoFlightsDto,
    UpdateFlightInMeasureDto,
} from 'app/model/CORE';
import TimeUtils from '../../../common/util/TimeUtils';
import { ModalPanelProps } from '../DashboardModal';
import KeyUtils from '../../../common/util/KeyUtils';
import SlotTable, { SLOT_ACTIONS_COLUMNS, SlotColumn, StandardSlotColumn } from '../../table/SlotTable';
import memoizeOne from 'memoize-one';
import { MULTIPLE_SELECTION, MultipleSelection, SortMode } from 'app/common/table/ManagedTable';
import './SlotListPanel.less';
import ConditionalRender from '../../../common/components/ConditionalRender';
import Utils from '../../../common/util/Utils';
import { CreateNewSlotPanel } from '../slot/CreateNewSlotPanel';
import AppContext from 'app/common/context/AppContext';
import { ShowModalEvent } from 'app/common/eventbus/Events';

const SLOT_LIST_COLUMNS: SlotColumn[] = [
    StandardSlotColumn.SLOT_TIME,
    StandardSlotColumn.SR,
    StandardSlotColumn.ARCID,
    StandardSlotColumn.ADEP,
    StandardSlotColumn.ADES,
    StandardSlotColumn.KIND,
    StandardSlotColumn.RE_LF_XFR,
    StandardSlotColumn.ETOT,
    StandardSlotColumn.CTOT,
    StandardSlotColumn.ATOT,
    StandardSlotColumn.ETO,
    StandardSlotColumn.STO,
    StandardSlotColumn.CTO,
    StandardSlotColumn.ATO,
    StandardSlotColumn.DELAY,
    StandardSlotColumn.REGS,
    StandardSlotColumn.LAST_MSG,
    StandardSlotColumn.MPR,
    SLOT_ACTIONS_COLUMNS,
];

enum SortType {
    ATO = 'ATO',
    CTO = 'CTO',
    STO = 'STO',
    ETO = 'ETO',
    TIME = 'Slot Time',
}

const compareSlotsByType = (slotA: Slot, slotB: Slot, sortType: SortType) => {
    switch (sortType) {
        case SortType.ATO:
            return TimeUtils.diffInMs(getSlotSortTimeByAto(slotA), getSlotSortTimeByAto(slotB));
        case SortType.CTO:
            return TimeUtils.diffInMs(getSlotSortTimeByCto(slotA), getSlotSortTimeByCto(slotB));
        case SortType.STO:
            return TimeUtils.diffInMs(getSlotSortTimeBySto(slotA), getSlotSortTimeBySto(slotB));
        case SortType.ETO:
            return TimeUtils.diffInMs(getSlotSortTimeByEto(slotA), getSlotSortTimeByEto(slotB));
        case SortType.TIME:
            return TimeUtils.diffInMs(slotA.referenceTime, slotB.referenceTime);
    }
};

const getSlotSortTimeByAto = (slot: Slot) => slot.regulatedFlight?.actualTimeOver || getSlotSortTimeByCto(slot);
const getSlotSortTimeByCto = (slot: Slot) =>
    slot.regulatedFlight?.calculatedTimeOver || slot.regulatedFlight?.actualTimeOver || getSlotSortTimeBySto(slot);
const getSlotSortTimeBySto = (slot: Slot) => slot.regulatedFlight?.scheduledTimeOver || getSlotSortTimeByEto(slot);
const getSlotSortTimeByEto = (slot: Slot) => slot.regulatedFlight?.estimatedTimeOver || slot.referenceTime;

const INITIAL_STATE: SlotListPanelState = {
    slots: null,
    sortBy: SortType.ATO,
    lastUpdate: null,
    loading: true,
};

interface SlotListPanelProps extends ModalPanelProps {
    measureId: MeasureId;
}

interface SlotListPanelState {
    loading: boolean;
    slots: Array<Slot>;
    selection?: Array<Slot>;
    sortBy: SortType;
    lastUpdate: Date;
}

export class SlotListPanel extends PureComponent<SlotListPanelProps, SlotListPanelState> {
    state = INITIAL_STATE;
    public static INITIAL_WIDTH = '105em';
    selection = memoizeOne(
        (selectedSlots: Slot[]): MultipleSelection<Slot> => ({
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.handleSelectionChange,
            selectedItems: selectedSlots,
        }),
    );

    componentDidMount(): void {
        this.refresh();
    }

    componentDidUpdate(prevProps) {
        if (this.props?.measureId !== prevProps?.measureId) {
            this.setState(INITIAL_STATE, () => this.refresh());
        }
    }

    refresh = () => {
        this.setState({ loading: true });
        const req: RetrieveSlotListsRequestDto = {
            dataset: AppContext.getDataset(),
            regulationIds: [this.props.measureId],
        };
        ServiceFactory.coreService()
            .retrieveSlotList(req)
            .then(reply => {
                if (reply?.error) {
                    notification.error({ message: 'Error', description: reply?.error.message, duration: 0 });
                } else if (reply?.data?.length === 1) {
                    // Extract all slots from slot sequences to a single array
                    const slotList = reply?.data[0];
                    const slots: Slot[] = slotList.slotSequences?.map(slotSequence => slotSequence.slots).flat();
                    this.setState({ slots: slots, lastUpdate: AppContext.getCurrentTime() });
                } else {
                    notification.error({
                        message: 'Error',
                        description: 'Expected 1 slot list, but retrieve none or multiple ones',
                        duration: 0,
                    });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };

    handleSelectionChange = (selection: Array<Slot>) => {
        this.setState({ selection: selection });
    };

    handlePromise = (promise: Promise<Reply<FlightResultList<UpdateFlightInMeasureDto>>>) => {
        promise
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                    this.setState({ loading: false });
                } else {
                    if (Utils.isNotEmpty(reply.data.failures)) {
                        reply.data.failures.forEach(failure => {
                            notification.error({
                                message: 'Error updating flight: ' + failure.flightKeys?.callsign,
                                description: failure.reason,
                            });
                        });
                        this.setState({ loading: false });
                    } else {
                        // This will reset the loading after it's done
                        this.refresh();
                    }
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
                this.setState({ loading: false });
            });
    };

    updateFlight = (request: UpdateFlightInMeasureDto) => {
        this.setState({
            loading: true,
        });
        this.handlePromise(
            ServiceFactory.stamCoreService().updateFlightsInMeasure({
                dataset: AppContext.getDataset(),
                day: AppContext.getMonitoredDay(),
                flights: [request],
            }),
        );
    };

    bookSlot = (slot: Slot, flightId: FlightId) => {
        const bookSlot: BookSlotInRegulationDto = {
            clazz: BOOK_SLOT_IN_REGULATION_DTO_CLAZZ_VALUE,
            flightKeys: flightId.flightKeys,
            regulationId: this.props.measureId.id,
            slotSequenceIndex: slot.slotSequenceIndex,
            slotTime: slot.referenceTime,
        };
        this.updateFlight(bookSlot);
    };

    freeSlot = (slot: Slot) => {
        const freeSlot: FreeFlightFromSlotDto = {
            clazz: FREE_FLIGHT_FROM_SLOT_DTO_CLAZZ_VALUE,
            flightKeys: slot.regulatedFlight.flightId.flightKeys,
            flightSlotSequenceIndex: slot.slotSequenceIndex,
            flightSlotTime: slot.referenceTime,
            regulationId: this.props.measureId.id,
        };
        this.updateFlight(freeSlot);
    };

    onSwapTwoFlights = () => {
        const flight = this.state.selection[0];
        const otherFlight = this.state.selection[1];
        const swapTwoFlights: SwapTwoFlightsDto = {
            clazz: SWAP_TWO_FLIGHTS_DTO_CLAZZ_VALUE,
            flightKeys: flight.regulatedFlight.flightId.flightKeys,
            flightSlotSequenceIndex: flight.slotSequenceIndex,
            flightSlotTime: flight.referenceTime,
            otherFlightKeys: otherFlight.regulatedFlight.flightId.flightKeys,
            otherFlightSlotSequenceIndex: otherFlight.slotSequenceIndex,
            otherFlightSlotTime: otherFlight.referenceTime,
            regulationId: this.props.measureId.id,
        };
        this.updateFlight(swapTwoFlights);
    };

    updateSlot = (slot: Slot, updateKind: SlotUpdateKind) => {
        this.setState({
            loading: true,
        });
        ServiceFactory.coreService()
            .updateSlot({
                dataset: AppContext.getDataset(),
                regulationId: this.props.measureId.id,
                slotSequenceIndex: slot.slotSequenceIndex,
                slotTime: slot.referenceTime,
                updateKind: updateKind,
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };

    createNewSlot = () => {
        const content = (
            <CreateNewSlotPanel
                onSuccess={v => {
                    // TODO Sent request to create a new slot in the reg
                    const request: CreateSlotInRegulationDto = {
                        clazz: CREATE_SLOT_IN_REGULATION_DTO_CLAZZ_VALUE,
                        regulationId: this.props.measureId.id,
                        flightSlotTime: v.slotTime,
                        forceEntryKind: ForceEntryType.TO,
                        forceEntryTime: v.slotTime,
                    };
                    this.updateFlight(request);
                }}
            />
        );
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '20em'));
    };

    isSwapSlotDisabled = (slots: Slot[]): boolean => {
        const nbFlightsSelected = slots?.filter(s => Utils.isExists(s.regulatedFlight));
        if (Utils.size(nbFlightsSelected) !== 2) return true;

        return nbFlightsSelected.some(
            slot =>
                slot.type === SlotType.OVERLOAD ||
                slot.regulatedFlight?.exempted ||
                Utils.isInList(
                    slot.regulatedFlight.cfmuFlightType,
                    CfmuFlightType.TERMINATED,
                    CfmuFlightType.ACT,
                    CfmuFlightType.TACT_ACTIVATED,
                ) ||
                TimeUtils.compare(slot.referenceTime, AppContext.getCurrentTime()) > 0,
        );
    };

    getSlotListButtons = () => {
        const buttons: ReactNode[] = [];

        buttons.push(
            <Button key="close" onClick={this.props.onClose}>
                Close
            </Button>,
        );
        buttons.push(
            <Button key="edit-regulation" disabled={true}>
                Edit regulation
            </Button>,
        );
        buttons.push(
            <Button key="swap" disabled={this.isSwapSlotDisabled(this.state.selection)} onClick={this.onSwapTwoFlights}>
                Swap selection
            </Button>,
        );
        buttons.push(
            <Button key="new" onClick={this.createNewSlot}>
                Create new slot
            </Button>,
        );

        return buttons;
    };

    render() {
        const slots = this.state.slots?.slice().sort((a, b) => compareSlotsByType(a, b, this.state.sortBy));

        return (
            <Flex direction="vertical" className="slot-list-panel overflow">
                <Spin spinning={this.state.loading}>
                    <Flex direction="vertical">
                        <Flex className="modal-title header" grow={false} shrink={false}>
                            <Flex size="15em" className="update-time">
                                <ConditionalRender condition={Utils.isExists(this.state.lastUpdate)}>
                                    last update{' '}
                                    {TimeUtils.formatDateDDMMYYYY(this.state.lastUpdate, '-') +
                                        ' ' +
                                        TimeUtils.formatHHMM(this.state.lastUpdate)}
                                </ConditionalRender>
                            </Flex>
                            <Flex direction="vertical">
                                <Flex className="title">SLOT LIST - {this.props.measureId?.id}</Flex>
                                <Flex style={{ justifyContent: 'center' }}>{/*TODO*/}</Flex>
                            </Flex>
                            <Flex size="15em" />
                        </Flex>
                        <Flex className="config" grow={false} shrink={false}>
                            <Select
                                className="measure-sortby"
                                value={this.state.sortBy}
                                onChange={value => this.setState({ sortBy: value })}>
                                {Object.keys(SortType).map(type => (
                                    <Select.Option key={type} value={type}>
                                        {SortType[type]}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Flex>
                        <Flex className="table">
                            <SlotTable
                                onBookSlot={this.bookSlot}
                                onFreeSlot={this.freeSlot}
                                onUpdateSlot={this.updateSlot}
                                data={slots}
                                columns={SLOT_LIST_COLUMNS}
                                keyProvider={KeyUtils.SLOT_KEY_PROVIDER}
                                columnResizeDisabled={true}
                                noDataMessage={'No slots'}
                                selection={this.selection(this.state.selection)}
                                sortMode={SortMode.NONE}
                            />
                        </Flex>
                        <Flex className="button-panel" grow={false} shrink={false}>
                            <Space>{this.getSlotListButtons()}</Space>
                        </Flex>
                    </Flex>
                </Spin>
            </Flex>
        );
    }
}
